/**
 * Vertriebscockpit.
 *
 * Bewusst kein Kanban-Board: gezeigt wird nur, was beim Kunden liegt.
 * Welche Vorgänge das sind, entscheidet allein das Feld `offen`, das der Server
 * berechnet – Liste, Kacheln und Chips zählen dieselbe Menge.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useKora, useMeta } from '../zustand';
import { IconEinstellungen, IconMonday, IconSharepoint } from '../komponenten/Icons';
import { dat, tageSeit } from '../hilfen/format';
import type { Angebot } from '../typen';
import './cockpit.css';


/** Spalten, nach denen sich die Liste sortieren lässt. */
type SortSpalte = 'nr' | 'ort' | 'status' | 'tage' | 'kalk' | 'vers';

export function Cockpit() {
  const { nutzer, modalOeffnen } = useKora();
  const meta = useMeta();
  const navigate = useNavigate();
  const [suchParameter, setzeSuchParameter] = useSearchParams();

  // Die Geschäftsführung startet in der Team-Ansicht, alle anderen bei "Nur meine".
  const [nurMeine, setNurMeine] = useState(!nutzer?.istAdmin);
  const [versFilter, setVersFilter] = useState('');

  // Sprung aus dem Team-Überblick: Team-Ansicht, gefiltert auf die Person.
  useEffect(() => {
    const person = suchParameter.get('versender');
    if (!person) return;
    setNurMeine(false);
    setVersFilter(person);
    setzeSuchParameter({}, { replace: true });
  }, [suchParameter, setzeSuchParameter]);

  const abfrage = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  const alle = useMemo(() => abfrage.data ?? [], [abfrage.data]);
  const meinName = nutzer?.name ?? '';

  /** Wer führt den Vorgang? Der Versender, ersatzweise der Kalkulant. */
  const fuehrt = (a: Angebot, name: string) => (a.versender || a.kalkulant) === name;
  const gehoert = (a: Angebot, name: string) => fuehrt(a, name) || a.kalkulant === name;

  const gefiltert = useMemo(() => {
    if (nurMeine) return alle.filter((a) => gehoert(a, meinName));
    if (versFilter) return alle.filter((a) => fuehrt(a, versFilter));
    return alle;
  }, [alle, nurMeine, versFilter, meinName]);

  const offene = useMemo(() => gefiltert.filter((a) => a.offen), [gefiltert]);

  /**
   * Freitextsuche über alles, was auf der Zeile steht: Nummer, Kategorie,
   * Kunde, Ort, Versender, Kalkulant. Mehrere Wörter müssen alle vorkommen,
   * aber nicht nebeneinander – „koller fe" findet 26067_FE_Koller.
   */
  const [suche, setSuche] = useState('');

  const gesucht = useMemo(() => {
    const worte = suche
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);
    if (worte.length === 0) return offene;

    return offene.filter((a) => {
      const heuhaufen = [
        a.kennung,
        a.prefix,
        a.kategorie,
        a.name,
        a.kunde.name,
        a.kunde.ort,
        a.kunde.kundenNr,
        a.kalkulant,
        a.versender,
        a.status,
      ]
        .join(' ')
        .toLowerCase();
      return worte.every((w) => heuhaufen.includes(w));
    });
  }, [offene, suche]);

  /**
   * Sortierung. Voreinstellung bleibt „Tage seit Versand, älteste zuerst" –
   * das ist der Zweck dieser Liste. Über die Spaltenköpfe umschaltbar.
   */
  const [sortSpalte, setSortSpalte] = useState<SortSpalte>('tage');
  const [absteigend, setAbsteigend] = useState(true);

  function sortiereNach(spalte: SortSpalte) {
    if (spalte === sortSpalte) {
      setAbsteigend((a) => !a);
      return;
    }
    setSortSpalte(spalte);
    // Zahlen sinnvoll voreinstellen: Tage absteigend (älteste oben),
    // Nummern aufsteigend (kleinste oben).
    setAbsteigend(spalte === 'tage');
  }

  const liste = useMemo(() => {
    const richtung = absteigend ? -1 : 1;
    const text = (s: string) => (s || '').toLocaleLowerCase('de-AT');

    return [...gesucht].sort((a, b) => {
      let v = 0;
      switch (sortSpalte) {
        case 'nr':
          // Nummer samt Variante: 26048 vor 26048a vor 26049.
          v = a.nr - b.nr || a.variante.localeCompare(b.variante);
          break;
        case 'ort':
          v = text(a.kunde.ort).localeCompare(text(b.kunde.ort), 'de-AT');
          break;
        case 'status':
          v = text(a.status).localeCompare(text(b.status), 'de-AT');
          break;
        case 'kalk':
          v = text(a.kalkulant).localeCompare(text(b.kalkulant), 'de-AT');
          break;
        case 'vers':
          v = text(a.versender).localeCompare(text(b.versender), 'de-AT');
          break;
        default:
          v = a.tageVersand - b.tageVersand;
      }
      // Gleichstand immer nach Nummer auflösen, damit die Reihenfolge
      // zwischen zwei Aufrufen stabil bleibt.
      if (v === 0) v = a.nr - b.nr || a.variante.localeCompare(b.variante);
      return v * richtung;
    });
  }, [gesucht, sortSpalte, absteigend]);

  const kpis = useMemo(() => {
    const automatisch = offene.reduce(
      (summe, a) =>
        summe +
        a.erinnerungen.filter((e) => e.art === 'auto' && tageSeit(e.datum, meta.heute) <= 7).length,
      0,
    );
    const anruf = offene.filter((a) => a.nachfass.anrufFaellig || a.wvFaellig).length;
    return [
      {
        label: 'Versendete Angebote',
        wert: String(offene.length),
        hinweis: 'offen beim Kunden, inkl. fälliger Wiedervorlagen',
        farbe: 'var(--eh-text)',
      },
      {
        label: 'Erinnerung automatisch',
        wert: String(automatisch),
        hinweis: 'diese Woche versendet',
        farbe: 'var(--eh-gruen-ampel)',
      },
      {
        label: 'Anruf ausstehend',
        wert: String(anruf),
        hinweis: 'inkl. fälliger Wiedervorlagen',
        farbe: 'var(--eh-rot)',
      },
    ];
  }, [offene, meta.heute]);

  const sortState = { aktiv: sortSpalte, absteigend, onSortieren: sortiereNach };

  const versChips = useMemo(() => {
    const alleOffen = alle.filter((a) => a.offen);
    const eintraege = [{ wert: '', label: 'Alle', zahl: alleOffen.length }].concat(
      meta.team.map((p) => ({
        wert: p.name,
        label: p.name,
        zahl: alleOffen.filter((a) => fuehrt(a, p.name)).length,
      })),
    );
    return eintraege.filter((c) => c.zahl > 0 || c.wert === '');
  }, [alle, meta.team]);

  return (
    <div className="eh-inhalt cockpit">
      <div className="eh-kopfzeile">
        <div>
          <div className="eh-kicker">Pipeline</div>
          <div className="eh-titel">Vertriebscockpit</div>
          <div className="cockpit-untertitel">
            Angebote, die beim Kunden liegen – und wo ein Nachfassen ansteht.
          </div>
        </div>
        <div className="cockpit-aktionen">
          <button type="button" className="cockpit-neu" onClick={() => navigate('/neu')}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>Neues Angebot
          </button>
          {nutzer?.istAdmin && (
            <button
              type="button"
              className="cockpit-zahnrad"
              title="Einstellungen"
              onClick={() => modalOeffnen({ art: 'einstellungen' })}
            >
              <IconEinstellungen groesse={17} />
            </button>
          )}
        </div>
      </div>

      <div className="cockpit-kpis">
        {kpis.map((k) => (
          <div className="cockpit-kpi" key={k.label}>
            <div className="cockpit-kpi-label">{k.label}</div>
            <div className="cockpit-kpi-zeile">
              <div className="cockpit-kpi-wert" style={{ color: k.farbe }}>
                {k.wert}
              </div>
              <div className="cockpit-kpi-hinweis">{k.hinweis}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="eh-kopfzeile">
        <div>
          <div className="cockpit-listentitel">Versendete Angebote</div>
          <div className="cockpit-listenhinweis">
            Sortiert nach Tagen seit Versand · beauftragte und abgesagte Angebote verschwinden aus
            dieser Liste
          </div>
        </div>
        <div className="cockpit-umschalt-zeile">
          <label className="cockpit-suche">
            <span className="cockpit-suche-lupe" aria-hidden="true">
              ⌕
            </span>
            <input
              type="search"
              value={suche}
              placeholder="Suchen: Nummer, Kunde, Ort, Gewerk …"
              aria-label="Angebote durchsuchen"
              onChange={(e) => setSuche(e.target.value)}
            />
            {suche && (
              <button
                type="button"
                className="cockpit-suche-weg"
                title="Suche zurücksetzen"
                onClick={() => setSuche('')}
              >
                ×
              </button>
            )}
          </label>
          <span className="cockpit-stand">Stand {meta.heuteText}</span>
          <div className="cockpit-umschalter">
            <button
              type="button"
              className={nurMeine ? '' : 'aktiv'}
              onClick={() => setNurMeine(false)}
            >
              Team
            </button>
            <button
              type="button"
              className={nurMeine ? 'aktiv' : ''}
              onClick={() => {
                setNurMeine(true);
                setVersFilter('');
              }}
            >
              Nur meine
            </button>
          </div>
        </div>
      </div>

      {!nurMeine && (
        <div className="eh-chips">
          {versChips.map((c) => (
            <button
              key={c.wert || 'alle'}
              type="button"
              className={`eh-chip ${versFilter === c.wert ? 'eh-chip--aktiv' : ''}`}
              onClick={() => setVersFilter(c.wert)}
            >
              {c.label}
              <span className="eh-chip-zahl">{c.zahl}</span>
            </button>
          ))}
        </div>
      )}

      <div className="cockpit-tabelle">
        {/* Spaltenklassen steuern, was bei welcher Fensterbreite entfällt –
            siehe die Breakpoints in cockpit.css. */}
        <div className="cockpit-zeile cockpit-kopf">
          <Kopf spalte="nr" klasse="sp-angebot" {...sortState}>
            Angebot
          </Kopf>
          <Kopf spalte="ort" klasse="sp-ort" {...sortState}>
            Ort
          </Kopf>
          <Kopf spalte="status" klasse="sp-status" {...sortState}>
            Status
          </Kopf>
          <Kopf spalte="tage" klasse="sp-tage" {...sortState}>
            Seit Versand
          </Kopf>
          <Kopf spalte="kalk" klasse="sp-kalk" {...sortState}>
            Kalkulant
          </Kopf>
          <Kopf spalte="vers" klasse="sp-vers" {...sortState}>
            Versender
          </Kopf>
          <div className="sp-erinn">Erinnerung</div>
          <div className="sp-links">Links</div>
        </div>

        {abfrage.isLoading && <div className="cockpit-leer">Angebote werden geladen …</div>}

        {liste.map((a) => (
          <Zeile key={a.id} a={a} onOeffnen={() => navigate(`/angebot/${a.id}`, { state: { herkunft: 'cockpit' } })} />
        ))}

        {/* Bei leerer Suche würde „kein Angebot beim Kunden" die falsche
            Ursache nennen – es liegen welche vor, sie passen nur nicht. */}
        {!abfrage.isLoading && liste.length === 0 && (
          <div className="cockpit-leer">
            {suche
              ? `Kein Treffer für „${suche}" unter ${offene.length} ${
                  offene.length === 1 ? 'Vorgang' : 'Vorgängen'
                }.`
              : 'Aktuell liegt kein Angebot beim Kunden.'}
          </div>
        )}
      </div>

      {suche && liste.length > 0 && (
        <div className="cockpit-trefferzeile">
          {liste.length} von {offene.length} angezeigt · gefiltert nach „{suche}"
          <button type="button" className="cockpit-suche-zuruecksetzen" onClick={() => setSuche('')}>
            alle anzeigen
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Klickbarer Spaltenkopf.
 *
 * Ein Klick sortiert nach dieser Spalte, ein zweiter dreht die Richtung um.
 * Der Pfeil steht nur an der aktiven Spalte – sonst sieht die Kopfzeile aus
 * wie ein Bedienfeld statt wie eine Tabelle.
 */
function Kopf({
  spalte,
  klasse,
  aktiv,
  absteigend,
  onSortieren,
  children,
}: {
  spalte: SortSpalte;
  klasse: string;
  aktiv: SortSpalte;
  absteigend: boolean;
  onSortieren: (s: SortSpalte) => void;
  children: React.ReactNode;
}) {
  const istAktiv = aktiv === spalte;
  return (
    <button
      type="button"
      className={`cockpit-kopfknopf ${klasse} ${istAktiv ? "aktiv" : ""}`}
      onClick={() => onSortieren(spalte)}
      aria-sort={istAktiv ? (absteigend ? "descending" : "ascending") : "none"}
      title={istAktiv ? "Richtung umkehren" : "Nach dieser Spalte sortieren"}
    >
      {children}
      <span className="cockpit-pfeil" aria-hidden="true">
        {istAktiv ? (absteigend ? "▾" : "▴") : ""}
      </span>
    </button>
  );
}

function Zeile({ a, onOeffnen }: { a: Angebot; onOeffnen: () => void }) {
  const meta = useMeta();
  const statusLabel = a.pausiert
    ? `Wiedervorlage bis ${dat(a.wiedervorlage)}`
    : a.wvFaellig
      ? 'Wiedervorlage'
      : a.status;
  const gelb = a.pausiert || a.wvFaellig;

  const anrufFaellig = a.nachfass.anrufFaellig && !a.wvFaellig;
  const ruhig = !anrufFaellig && !a.wvFaellig;

  const erinnText = !meta.aktiveStatus.includes(a.status)
    ? '—'
    : a.nachfass.antwortAm
      ? 'Antwort erkannt'
      : !a.nachfass.aktiv
        ? 'pausiert'
        : a.nachfass.mailVerfallen
          ? 'Erinnerung entfällt'
          : a.nachfass.erledigt < 1
            ? `1. autom. am ${dat(a.nachfass.mailAm)}`
            : `Anruf ab ${dat(a.nachfass.anrufAb)}`;

  const letzteNotiz = a.anrufe[0]?.notiz ?? '';

  return (
    <div className="cockpit-zeile cockpit-datenzeile">
      {/* title als Rückfallebene: bei sehr langen Firmennamen bleibt der volle
          Text erreichbar, auch wenn die Spalte ihn kürzen muss. */}
      <button
        type="button"
        className="cockpit-name sp-angebot"
        onClick={onOeffnen}
        title={a.name}
      >
        {/* Der Punkt trägt den Status auch dann, wenn die Statusspalte auf
            schmalen Fenstern entfällt – deshalb hier als Tooltip. */}
        <span
          className="cockpit-punkt"
          style={{ background: a.statusPunkt }}
          title={statusLabel}
        />
        <span>{a.name}</span>
      </button>

      <div className="cockpit-ort sp-ort" title={a.ort}>
        {a.ort}
      </div>

      <div className="sp-status">
        <span className={`cockpit-status ${gelb ? 'gelb' : ''}`}>{statusLabel}</span>
      </div>

      <div className="sp-tage" data-label="Seit Versand">
        <span
          className="cockpit-ampel"
          style={{ background: a.ampel.bg, color: a.ampel.fg }}
        >
          {a.tageVersandText}
        </span>
      </div>

      {/* Nur das Kürzel, kein ausgeschriebener Name. MB, MZ und TPK sind
          eindeutig, und der Platz gehört der Angebotsbezeichnung. Der volle
          Name steht im Tooltip. */}
      <div className="cockpit-person sp-kalk" data-label="Kalkulant">
        <span
          className="eh-avatar cockpit-avatar"
          title={`Kalkulant: ${a.kalkulant} · Profil aus Monday`}
        >
          {a.kalkInitialen}
        </span>
      </div>

      {/* Eigene Spalte statt zweitem Avatar beim Kalkulanten: in wessen Namen
          die Erinnerung hinausgeht, ist zu wichtig für ein Tooltip. */}
      <div className="cockpit-person sp-vers" data-label="Versender">
        {a.versender ? (
          <span
            className="eh-avatar eh-avatar--versender cockpit-avatar"
            title={
              a.versManuell
                ? `Versender: ${a.versender} · von Hand eingetragen`
                : `Versender: ${a.versender} · aus Monday ermittelt`
            }
          >
            {a.versInitialen}
          </span>
        ) : (
          <span className="cockpit-personleer" title="Noch nicht versendet oder nicht ermittelbar">
            —
          </span>
        )}
      </div>

      <div className="sp-erinn" data-label="Erinnerung">
        {anrufFaellig && (
          <button type="button" className="cockpit-erinn rot" onClick={onOeffnen}>
            Anruf ausstehend
          </button>
        )}
        {a.wvFaellig && (
          <button
            type="button"
            className="cockpit-erinn gelb"
            title={letzteNotiz}
            onClick={onOeffnen}
          >
            Wiedervorlage fällig
          </button>
        )}
        {ruhig && <span className="cockpit-erinn-text">{erinnText}</span>}
      </div>

      <div className="cockpit-links sp-links">
        {a.mondayUrl ? (
          <a href={a.mondayUrl} target="_blank" rel="noreferrer" title="Monday-Item">
            <IconMonday />
          </a>
        ) : (
          <span className="cockpit-link-leer" title="Kein Monday-Item verknüpft">
            <IconMonday />
          </span>
        )}
        {a.sharepointUrl ? (
          <a href={a.sharepointUrl} target="_blank" rel="noreferrer" title="SharePoint-Ordner">
            <IconSharepoint />
          </a>
        ) : (
          <span className="cockpit-link-leer" title="Kein SharePoint-Ordner verknüpft">
            <IconSharepoint />
          </span>
        )}
      </div>
    </div>
  );
}
